import { getSupabaseClient } from "../../providers/supabase/supabase";

export type CopilotContext = {
  offerName?: string | null;
  contactName?: string | null;
  companyName?: string | null;
};

export type Suggestion = {
  objecion: string | null;
  respuesta: string;
  siguiente_pregunta: string;
};

export type Summary = {
  puntos_clave: string[];
  objeciones: string[];
  compromiso: string | null;
  siguiente_paso: string | null;
  fecha_sugerida: string | null;
};

type ActionResult<T> = { data: T | null; error: string | null };

/** Llama a una acción del Copiloto en la función "ai" (Bloque C2, paso 5). */
const invoke = async <T>(
  action: "copilot_suggest" | "copilot_summary",
  body: Record<string, unknown>,
): Promise<ActionResult<T>> => {
  const { data, error, response } =
    await getSupabaseClient().functions.invoke<T>("ai", {
      body: { action, ...body },
    });
  if (error) {
    const parsed = response
      ? await response.json().catch(() => null as { message?: string } | null)
      : null;
    return {
      data: null,
      error: parsed?.message ?? "No se pudo completar la solicitud.",
    };
  }
  return { data: data ?? null, error: null };
};

export const requestSuggestion = (
  consentId: number,
  transcript: string,
  context: CopilotContext,
) =>
  invoke<Suggestion>("copilot_suggest", {
    consent_id: consentId,
    transcript,
    context,
  });

export const requestSummary = (
  consentId: number,
  transcript: string,
  context: CopilotContext,
) =>
  invoke<Summary>("copilot_summary", {
    consent_id: consentId,
    transcript,
    context,
  });

/** Texto editable del resumen (lo que se guarda como nota). */
export const formatSummaryText = (s: Summary): string =>
  [
    s.puntos_clave.length
      ? `Puntos clave:\n${s.puntos_clave.map((p) => `- ${p}`).join("\n")}`
      : "",
    s.objeciones.length
      ? `Objeciones:\n${s.objeciones.map((o) => `- ${o}`).join("\n")}`
      : "",
    s.compromiso ? `Compromiso: ${s.compromiso}` : "",
    s.siguiente_paso ? `Siguiente paso: ${s.siguiente_paso}` : "",
    s.fecha_sugerida ? `Fecha sugerida: ${s.fecha_sugerida}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");

/** "Guardar como nota en el contacto": usa la tabla de notas que ya tiene el CRM. */
export const saveSummaryAsNote = async (
  contactId: number,
  salesId: number,
  text: string,
) => {
  const { error } = await getSupabaseClient().from("contact_notes").insert({
    contact_id: contactId,
    sales_id: salesId,
    text,
    date: new Date().toISOString(),
  });
  if (error) throw new Error(error.message);
};
